import { FILTER_FEATURE } from "api/ApiPath";
import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import dayjs from "dayjs";
import { ErrorCodeGeneralInjection } from "features/errorCodeGeneralFeature/di/ErrorCodeGeneralInjection";
import { DetailErrorCodeGeneralEntity } from "features/errorCodeGeneralFeature/domain/entities/DetailErrorCodeGeneralEntity";
import { ErrorCodeGeneralRequestEntity } from "features/errorCodeGeneralFeature/domain/entities/ErrorCodeGeneralRequestEntity";
import { ErrorCodeGeneralResponseEntity } from "features/errorCodeGeneralFeature/domain/entities/ErrorCodeGeneralResponseEntity";
import { PartnerResponseEntity } from "features/errorCodeGeneralFeature/domain/entities/PartnerResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { FeatureFormat } from "utils/format/FeatureFormat";
import {
  checkClipboardReadPermission,
  getMessage,
  paramsChecking,
} from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

class ErrorCodeGeneralInput implements Disposable {
  public fetchPartner = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchService = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchGeneral = new BehaviorSubject<ErrorCodeGeneralRequestEntity>({});
  public fetchDetail = new BehaviorSubject<ErrorCodeGeneralRequestEntity>({});
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public generalLoading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public fetchReport = new BehaviorSubject<ErrorCodeGeneralRequestEntity>({});

  [Symbol.dispose](): void {
    this.generalLoading.complete();
    this.detailLoading.complete();
    this.fetchCarrier.complete();
    this.fetchService.complete();
    this.fetchGeneral.complete();
    this.fetchDetail.complete();
    this.fetchPartner.complete();
    this.fetchReport.complete();
  }
}

class ErrorCodeGeneralOutput implements Disposable {
  public savedGenerals = new BehaviorSubject<
    Array<ErrorCodeGeneralResponseEntity>
  >([]);
  public savedDetails = new BehaviorSubject<
    Array<DetailErrorCodeGeneralEntity>
  >([]);
  public savedPartner = new BehaviorSubject<Array<PartnerResponseEntity>>([]);
  public savedCarrier = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedService = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedDetailTotal = new BehaviorSubject<number>(0);
  public isExportSuccess = new BehaviorSubject<boolean>(false);
  public isError = new BehaviorSubject<Error>({});

  [Symbol.dispose](): void {
    this.savedCarrier.complete();
    this.savedDetails.complete();
    this.savedPartner.complete();
    this.savedCarrier.complete();
    this.savedService.complete();
    this.savedDetailTotal.complete();
    this.isExportSuccess.complete();
    this.isError.complete();
  }
}

@injectable()
export class ErrorCodeGeneralViewModel extends BaseViewModel<
  ErrorCodeGeneralInput,
  ErrorCodeGeneralOutput
> {
  constructor() {
    super(new ErrorCodeGeneralInput(), new ErrorCodeGeneralOutput());
  }
  private useCase = ErrorCodeGeneralInjection.getUseCase();

  private downloadExcel(data: Blob, partnerName: string) {
    const url = window.URL.createObjectURL(data as Blob);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute(
      "download",
      `ERROR_CODE_REPORT_${!!partnerName ? partnerName : ""}${timestamp}.xlsx`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  public clickToCopy = async (value: string) => {
    let permission = await checkClipboardReadPermission();
    if (permission === "granted") {
      await window?.navigator?.clipboard
        ?.writeText(value as string)
        .then(() =>
          getMessage(
            `${strings.FileManagement.Placeholder.Url} - ${value}`,
            3,
            "success",
          ),
        );
    }
  };

  public checkPartnerType = (
    refCode: string,
    partnerList: Array<PartnerResponseEntity>,
  ) => {
    const partnerObj = {} as ErrorCodeGeneralRequestEntity;
    const requestEntity = new ErrorCodeGeneralRequestEntity();
    const partner = partnerList?.find(
      (info) => info?.partnerRefCode === refCode || info?.code === refCode,
    );
    if (requestEntity.isNeci(partner?.providerCode)) {
      partnerObj["partnerNeci"] = paramsChecking(refCode, "select");
      return partnerObj;
    } else if (requestEntity.isSMM(partner?.providerCode)) {
      partnerObj["partnerSMM"] = paramsChecking(refCode, "select");
      return partnerObj;
    } else if (
      requestEntity.isPartnerB2B(partner?.providerCode, partner?.type)
    ) {
      partnerObj["partnerB2B"] = paramsChecking(refCode, "select");
      return partnerObj;
    } else if (
      requestEntity.isSingleUserB2C(partner?.providerCode, partner?.type) &&
      !partner?.isStore
    ) {
      partnerObj["singleUserB2C"] = paramsChecking(refCode, "select");
      return partnerObj;
    } else if (
      requestEntity.isPartnerChainB2C(partner?.type) &&
      partner?.isStore
    ) {
      partnerObj["partnerChainB2C"] = paramsChecking(refCode, "select");
      return partnerObj;
    } else if (
      requestEntity.isPartnerGuest(partner?.providerCode, partner?.type)
    ) {
      partnerObj["partnerGuest"] = paramsChecking(refCode, "select");
      return partnerObj;
    } else {
      return {};
    }
  };

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchGeneral, async (params, guard) => {
      try {
        guard?.runIfLatest(() => this.input.generalLoading.next(true));
        const res = await this.useCase.fetchGeneral(params);
        if (res?.data) {
          this.output.savedGenerals.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.savedGenerals.next([]);
        this.output.isError.next(error);
      } finally {
        guard?.runIfLatest(() => this.input.generalLoading.next(false));
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params, guard) => {
      try {
        this.output.savedDetails.next([]);
        this.output.savedDetailTotal.next(0);
        guard?.runIfLatest(() => this.input.detailLoading.next(true));
        const res = await this.useCase.fetchDetail(params);
        if (res?.data) {
          this.output.savedDetails.next(res?.data);
          this.output.savedDetailTotal.next(res?.data?.length ?? 0);
        }
      } catch (error) {
        console.error(error);
        this.output.savedDetails.next([]);
        this.output.savedDetailTotal.next(0);
        this.output.isError.next(error);
      } finally {
        guard?.runIfLatest(() => this.input.detailLoading.next(false));
      }
    });

    this.skipFirstInitial(this.input.fetchReport, async (params) => {
      try {
        const res = await this.useCase.fetchReport(params);
        if (res?.data) {
          if (res?.data?.size < 1000) {
            getMessage(strings.GlobalMessage.ExcelError, 3, "error");
            return;
          } else {
            this.downloadExcel(res?.data, params?.partner);
            this.output.isExportSuccess.next(true);
          }
        }
      } catch (error) {
        console.error(error);
        this.output.isError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        await FeatureFormat.fetchFilterList({
          dataState: (value) => this.output.savedCarrier.next(value),
          path: FILTER_FEATURE.CARRIER_LIST,
          savedKey: CacheKeyEnum.carrierList,
          isNeedFeature: false,
          isForceRefresh: false,
          params,
        });
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchService, async (params) => {
      try {
        await FeatureFormat.fetchFilterList<
          BaseFilterListResponseModel,
          BaseFilterListRequestModel
        >({
          dataState: (value) => {
            let filteredVal = value?.filter(
              (item) => item?.code !== ServiceEnum.ADD_BALANCE,
            );
            this.output.savedService.next(filteredVal);
          },
          path: FILTER_FEATURE.SERVICE_LIST,
          savedKey: CacheKeyEnum.serviceList,
          isNeedFeature: true,
          isForceRefresh: false,
          params,
        });
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      try {
        await FeatureFormat.fetchFilterList<
          PartnerResponseEntity,
          BaseFilterListRequestModel
        >({
          dataState: (value) => this.output.savedPartner.next(value),
          path: FILTER_FEATURE.PARTNER_LIST,
          savedKey: CacheKeyEnum.partnerList,
          isNeedFeature: true,
          isForceRefresh: false,
          params,
        });
      } catch (error) {
        console.error(error);
      }
    });
  }
}
